import { ChevronLeft } from "@tamagui/lucide-icons";
import type { ReactNode } from "react";
import { XStack, type XStackProps, YStack } from "tamagui";

import { CloseButton } from "@/components";
import { Button } from "@/components/Button";
import ProgressBar from "@/components/ProgressBar";

export type TemplatedDialogControlsProps = XStackProps & {
  children?: ReactNode;
  onClose?: () => void;
  showBackButton?: boolean;
  onBackPress?: () => void;
  backButtonDisabled?: boolean;
  hideCloseButton?: boolean;
  dataTrackingIdPrefix?: string;
};

/** The back (left) + close (right) row shared by the dialog templates */
export const TemplatedDialogControls = ({
  children,
  onClose,
  showBackButton = false,
  onBackPress,
  backButtonDisabled = false,
  hideCloseButton,
  dataTrackingIdPrefix,
  ...props
}: TemplatedDialogControlsProps) => (
  <XStack
    alignItems="center"
    justifyContent="space-between"
    position="relative"
    {...props}
  >
    {showBackButton && onBackPress ? (
      <Button.Wrapper
        onPress={onBackPress}
        disabled={backButtonDisabled}
        aria-label="Back"
        alignItems="center"
        justifyContent="center"
        opacity={backButtonDisabled ? 0.4 : 1}
      >
        <ChevronLeft size="$300" />
      </Button.Wrapper>
    ) : (
      <YStack />
    )}
    {children && (
      <XStack
        position="absolute"
        left={0}
        right={0}
        alignItems="center"
        justifyContent="center"
        pointerEvents="none"
      >
        {children}
      </XStack>
    )}
    {onClose && !hideCloseButton && (
      <CloseButton
        onClose={onClose}
        dataTrackingId={
          dataTrackingIdPrefix
            ? `${dataTrackingIdPrefix}-close-button`
            : undefined
        }
      />
    )}
  </XStack>
);

export type DialogProgress = {
  value: number;
  label?: string;
};

export type TemplatedDialogHeaderProps = {
  children: ReactNode;
  onClose?: () => void;
  showBackButton?: boolean;
  onBackPress?: () => void;
  backButtonDisabled?: boolean;
  hideCloseButton?: boolean;
  dataTrackingIdPrefix?: string;
  progress?: DialogProgress;
};

const TemplatedDialogHeader = ({
  children,
  onClose,
  showBackButton = false,
  onBackPress,
  backButtonDisabled = false,
  hideCloseButton,
  dataTrackingIdPrefix,
  progress,
}: TemplatedDialogHeaderProps) => (
  <YStack paddingTop="$200" paddingHorizontal="$200">
    <TemplatedDialogControls
      onClose={onClose}
      showBackButton={showBackButton}
      onBackPress={onBackPress}
      backButtonDisabled={backButtonDisabled}
      hideCloseButton={hideCloseButton}
      dataTrackingIdPrefix={dataTrackingIdPrefix}
    >
      {children}
    </TemplatedDialogControls>
    <YStack paddingTop="$100" paddingBottom="$100" width="100%">
      <ProgressBar
        aria-label={progress?.label ?? "Profile completion progress"}
        value={progress?.value ?? 0}
        max={100}
        trackColor="$tileBackground"
      />
    </YStack>
  </YStack>
);

export default TemplatedDialogHeader;
